import {
  cannectDefaultProduct,
  cannectDefaultProductCapsule,
  cannectDefaultProductCream,
  cannectDefaultProductGummy,
  cannectDefaultProductNoTarg
} from '@cannect/assets/img'
import { useAuth } from '@cannect/hooks/AuthContext'
import { SoldByPillText } from '@cannect/new-components/atoms'
import { usePrepareCart } from 'hooks/usePrepareCart'
import { FlavorQuantitySelector } from 'new-components/FlavorQuantitySelector'
import { ProgressiveDiscountBadge } from 'new-components/ProgressiveDiscountBadge'
import { ProgressiveSingleQuantityControl, useProgressiveUnitPrice } from 'new-components/ProgressiveQuantityDropdown'
import { LuTrash2 } from 'react-icons/lu'
import Button from 'new-components/Button'
import React from 'react'
import cannectDefaultProductOil from '../../../../../assets/img/cannect-default-no-targ-oil.png'
import { isProgramOrder } from 'pages/WeightLossProgram/constants'
import { ProgramCartItem } from './ProgramCartItem'

interface CartItem {
  id: number
  uuid: string
  quantity: number
  unitPrice: string
  discount: string
  amount: string
  type: string
  product: {
    id: number
    name: string
    translated_name: string
    price: number
    real_price: number
    flavor: any
    media: Array<{
      path: string
    }>
    flavor_id: number
    main_product_id?: number
    info?: string
    origin?: string
  }
  promotion?: any
}

interface CartSectionProps {
  itens: CartItem[]
  onRemoveItem: (index: number) => void
  onRemoveAll: () => void
  onUpdateQuantity: (index: number, quantity: number) => void
  isUpdate: boolean
  coupon: any
  excludeProductIds?: Set<number>
}

/**
 * Bloco de preço (riscado + badge % + valor unitário) do item do carrinho.
 * Precedência de exibição: cupom > progressivo ATIVO (faixa atingida, preço menor que fallbackPrice)
 * > promoção > preço normal. O progressivo só "vence" a promoção quando de fato reduziu o preço —
 * caso contrário mantém o comportamento anterior (promoção prevalece).
 */
function CartItemPriceBlock({
  productId,
  quantity,
  fallbackPrice,
  isCouponDiscounted,
  couponUnitPrice,
  couponHasDiscount,
  couponDiscountPct,
  promotionData
}: {
  productId: number
  quantity: number
  fallbackPrice: number
  isCouponDiscounted: boolean
  couponUnitPrice: number
  couponHasDiscount: boolean
  couponDiscountPct: number
  promotionData: any
}) {
  const effectivePrice = useProgressiveUnitPrice(productId, quantity, fallbackPrice)
  const isProgressiveActive = effectivePrice < fallbackPrice
  const hasPromotion = !!promotionData?.promotional_price
  const progressiveDiscountPct = isProgressiveActive
    ? Math.round(((fallbackPrice - effectivePrice) / fallbackPrice) * 100)
    : 0

  let finalPrice = fallbackPrice
  if (isCouponDiscounted) {
    finalPrice = couponUnitPrice
  } else if (isProgressiveActive) {
    finalPrice = effectivePrice
  } else if (hasPromotion) {
    finalPrice = parseFloat(promotionData.promotional_price)
  }

  return (
    <div className="flex flex-col items-end gap-1">
      {couponHasDiscount && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 line-through">R$ {fallbackPrice.toFixed(2).replace('.', ',')}</span>
          <span className="text-xs font-normal italic text-primary-500">-{couponDiscountPct}%</span>
        </div>
      )}
      {!isCouponDiscounted && isProgressiveActive && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 line-through">R$ {fallbackPrice.toFixed(2).replace('.', ',')}</span>
          <span className="text-xs font-normal italic text-primary-500">-{progressiveDiscountPct}%</span>
        </div>
      )}
      {!isCouponDiscounted && !isProgressiveActive && hasPromotion && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 line-through">
            R${' '}
            {parseFloat(promotionData?.regular_price?.toString() || '0')
              .toFixed(2)
              .replace('.', ',')}
          </span>
          {promotionData?.discount_type === 'percent' ? (
            <span className="text-xs font-normal italic text-primary-500">-{promotionData?.discount_value || 0}%</span>
          ) : (
            <span className="text-xs font-normal italic text-primary-500">
              - R${' '}
              {parseFloat(promotionData?.discount_value || '0')
                .toFixed(2)
                .replace('.', ',')}
            </span>
          )}
        </div>
      )}
      <span className="text-base font-semibold text-gray-900">R$ {finalPrice.toFixed(2).replace('.', ',')}</span>
    </div>
  )
}

export const CartSection: React.FC<CartSectionProps> = ({
  itens,
  onRemoveItem,
  onRemoveAll,
  onUpdateQuantity: _onUpdateQuantity,
  isUpdate,
  coupon: _coupon,
  excludeProductIds
}) => {
  const filteredItens = excludeProductIds?.size
    ? itens.filter((item) => !excludeProductIds.has(item.product.id))
    : itens
  const { incrementOrDecrementCart, loading, cartLoading, coupon, lastOrderCreated } = usePrepareCart()

  const { role } = useAuth()

  const getProductImage = (product: any) => {
    const isNationalProduct = product?.product?.supplier?.id === 18
    const defaultImage = isNationalProduct ? cannectDefaultProduct : cannectDefaultProductNoTarg
    const imgMedia = product?.product?.media?.find((m: any) => m?.format === 'IMG')?.path
    const shapeNameFormatted = product?.product?.shape_id
    const isSupplementProduct = product?.product?.class_id === 6
    const isPillProduct = product?.product?.origin === 'pill'

    const isPatientUserType = role === 1

    let imageSource = ''

    if (isPillProduct) {
      return imgMedia
    }

    switch (shapeNameFormatted) {
      case 1:
        imageSource =
          isPatientUserType && !isSupplementProduct ? cannectDefaultProductOil : imgMedia || cannectDefaultProductOil
        break
      case 2:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductCapsule
            : imgMedia || cannectDefaultProductCapsule
        break
      case 4:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductCream
            : imgMedia || cannectDefaultProductCream
        break
      case 6:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductGummy
            : imgMedia || cannectDefaultProductGummy
        break
      default:
        imageSource = isPatientUserType && !isSupplementProduct ? defaultImage : imgMedia || defaultImage
        break
    }

    return imageSource
  }

  if (loading) {
    return (
      <div className="overflow-hidden rounded-xl bg-white">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="h-6 w-32 animate-pulse rounded bg-gray-200" />
          <div className="h-6 w-24 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="space-y-4 p-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="h-24 w-24 animate-pulse rounded-lg bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="space-y-2">
                <div className="h-6 w-6 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-visible rounded-xl bg-white">
      <div className="flex items-center justify-between px-4 py-1 sm:px-6">
        <h2 className="text-base font-semibold text-gray-900 sm:text-lg">
          Carrinho{' '}
          <span className="ml-1 text-xs font-normal text-gray-500 sm:text-sm">
            {itens.length} {itens.length === 1 ? 'item' : 'itens'}
          </span>
        </h2>

        {itens.length > 0 && (
          <button
            onClick={onRemoveAll}
            disabled={cartLoading || isUpdate}
            className="flex cursor-pointer items-center gap-1 rounded-md border-0 bg-transparent px-2 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-3">
            <LuTrash2 className="h-3 w-3 text-[#E87883] sm:h-4 sm:w-4" />
            <span className="font-poppins text-[10px] font-normal leading-[140%] text-[#E87883] sm:text-[12px]">
              {cartLoading ? 'Atualizando...' : 'Remover tudo'}
            </span>
          </button>
        )}
      </div>

      {itens.length === 0 ? (
        <div className="flex flex-col items-center px-4 py-8 text-center sm:px-6 sm:py-12">
          <p className="mb-4 text-sm text-gray-600 sm:mb-6 sm:text-base">Seu carrinho está vazio</p>
          <Button
            onClick={() => {
              window.location.href = '/produtos/1'
            }}>
            Continuar comprando
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {filteredItens.map((item) => {
            const index = itens.indexOf(item)
            const productData = (item as any).product || {}
            const promotionData = item.promotion || {}
            const isCombo = productData?.is_combo
            const comboProducts = (productData?.combo_products || []).filter(
              (cp: any) => !cp.product?.type || cp.product.type === 'PRODUTO'
            )

            // Item coberto por cupom (discount_type !== 'progressive'): o backend já resolveu o preço
            // da linha — usa item.amount/item.discount reais em vez de promotion/progressivo.
            const isCouponDiscounted =
              !!coupon && !!(item as any)?.discount_type && (item as any).discount_type !== 'progressive'
            const couponQty = Number(item.quantity) || 1
            const couponBasePrice =
              productData?.real_price || productData?.price || parseFloat((item as any).unitPrice || '0')
            const couponAmount = parseFloat((item as any)?.amount || '0')
            const couponDiscountTotal = parseFloat((item as any)?.discount || '0')
            let couponUnitPrice = couponBasePrice
            if (couponAmount > 0) {
              couponUnitPrice = couponAmount / couponQty
            } else if (couponDiscountTotal > 0) {
              couponUnitPrice = Math.max(0, couponBasePrice - couponDiscountTotal / couponQty)
            }

            // Percentual do cupom derivado do antes/depois real — sempre consistente com o preço
            // final exibido, independente de qual campo (amount ou discount) definiu couponUnitPrice.
            const couponHasDiscount = isCouponDiscounted && couponUnitPrice < couponBasePrice
            const couponDiscountPct =
              couponHasDiscount && couponBasePrice > 0
                ? Math.round(((couponBasePrice - couponUnitPrice) / couponBasePrice) * 100)
                : 0

            const isUnavailable = ((item as any)?.sellable ?? (productData as any)?.sellable ?? true) === false
            const isProgramItem = isProgramOrder(lastOrderCreated) && (productData as any)?.type === 'SERVICO'

            if (isProgramItem) {
              return (
                <div key={item.id || index}>
                  <ProgramCartItem />
                </div>
              )
            }

            return (
              <div key={item.id || index} className={isUnavailable ? 'bg-red-50' : ''}>
                {isUnavailable && (
                  <div className="flex items-center justify-between px-4 pt-3 sm:px-6">
                    <p className="text-xs font-medium text-red-600">Este produto está indisponível no momento.</p>
                    <button
                      onClick={() => onRemoveItem(index)}
                      disabled={cartLoading || isUpdate}
                      className="flex cursor-pointer items-center gap-1 rounded-md border-0 bg-transparent px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50">
                      <LuTrash2 className="h-4 w-4 text-[#E87883]" />
                      <span className="font-sans text-xs font-normal leading-[140%] text-[#E87883]">Remover</span>
                    </button>
                  </div>
                )}
                <div className="relative flex gap-4 overflow-visible px-4 py-4 sm:px-6 sm:py-5">
                  {/* Imagem do produto */}
                  <img
                    src={getProductImage(item)}
                    alt={productData?.translated_name || productData?.name || 'Produto'}
                    className="h-20 w-20 shrink-0 rounded-lg border border-gray-200 object-cover sm:h-24 sm:w-24"
                  />

                  {/* Conteúdo principal */}
                  <div className="flex flex-1 flex-col gap-2">
                    {/* Nome do produto */}
                    <ProgressiveDiscountBadge productId={productData.id} />
                    <h3 className="text-xxs font-semibold text-gray-900 sm:text-sm">
                      {productData?.translated_name || productData?.name || 'Nome do Produto'}
                    </h3>

                    {productData?.origin === 'pill' && <SoldByPillText />}

                    {isCombo && (
                      <span className="inline-flex w-fit items-center gap-1 rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600">
                        Combo &middot; {comboProducts.length} produtos
                      </span>
                    )}

                    {/* Seletor de sabor + quantidade */}
                    {item.type === 'PRODUTO' && !isCombo && (
                      <FlavorQuantitySelector
                        productId={productData.id}
                        mainProductId={productData?.main_product_id}
                        currentFlavorLabel={productData.flavor}
                      />
                    )}
                  </div>

                  {/* Lado direito: Remover, Quantidade e Preços */}
                  <div className="flex flex-col items-end justify-between gap-3">
                    {/* Botão Remover (topo direito) */}
                    {(item.type === 'PRODUTO' || item.type === 'TESTE_GENETICO') && (
                      <button
                        onClick={() => onRemoveItem(index)}
                        disabled={cartLoading || isUpdate}
                        className="flex cursor-pointer items-center gap-1 rounded-md border-0 bg-transparent px-2 py-1 text-sm transition-all disabled:cursor-not-allowed disabled:opacity-50">
                        <LuTrash2 className="h-4 w-4 text-[#E87883]" />
                        <span className="font-sans text-xs font-normal leading-[140%] text-[#E87883]">Remover</span>
                      </button>
                    )}

                    {/* Seletor de quantidade */}
                    {(item.type === 'PRODUTO' || item.type === 'TESTE_GENETICO') && (
                      <ProgressiveSingleQuantityControl
                        productId={productData.id}
                        qty={item.quantity || 1}
                        stock={productData?.stock}
                        basePrice={
                          productData?.real_price || productData?.price || parseFloat((item as any).unitPrice || '0')
                        }
                        onSelect={(n) => incrementOrDecrementCart(item.product.id, n)}
                      />
                    )}

                    {/* Preços (parte inferior direita) */}
                    <CartItemPriceBlock
                      productId={productData.id}
                      quantity={item.quantity || 1}
                      fallbackPrice={couponBasePrice}
                      isCouponDiscounted={isCouponDiscounted}
                      couponUnitPrice={couponUnitPrice}
                      couponHasDiscount={couponHasDiscount}
                      couponDiscountPct={couponDiscountPct}
                      promotionData={promotionData}
                    />
                  </div>
                </div>

                {/* Produtos do combo */}
                {isCombo && comboProducts.length > 0 && (
                  <div className="mx-4 mb-4 overflow-hidden rounded-xl border border-neutral-200 bg-white sm:mx-6">
                    <div className="border-b border-neutral-100 bg-neutral-50 px-4 py-2">
                      <span className="text-[11px] font-semibold text-neutral-500">Produtos incluídos neste combo</span>
                    </div>
                    <div className="space-y-2 p-3">
                      {comboProducts.map((cp: any, idx: number) => (
                        <div
                          key={cp.id}
                          className="flex items-center gap-3 rounded-lg border border-neutral-100 bg-neutral-50/50 px-3 py-2">
                          <div className="relative shrink-0">
                            <img
                              src={cp.product?.media?.[0]?.path || getProductImage(item)}
                              alt={cp.product?.translated_name || cp.product?.name}
                              className="h-11 w-11 rounded-lg border border-neutral-100 bg-white object-contain p-1"
                            />
                            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-700 text-[8px] font-bold text-white">
                              {idx + 1}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold text-neutral-800">
                              {cp.product?.translated_name || cp.product?.name}
                            </p>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600">
                                {cp.quantity || 1} {(cp.quantity || 1) === 1 ? 'unidade' : 'unidades'}
                              </span>
                              {cp.flavor?.name && (
                                <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600">
                                  {cp.flavor.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
